import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    getIcs2ReferralResponseHistory,
    listIcs2CustomsRequests,
} from 'store/declarations/eu/ics2/customs-requests/client';
import { describeReferralError } from 'store/declarations/eu/ics2/customs-requests/errors';
import {
    Ics2CustomsRequest,
    Ics2Referral,
    Ics2ReferralSubmission,
} from 'store/declarations/eu/ics2/customs-requests/types';
import { Ics2ReferralRow } from '../referralDocuments';
import { getDispatchErrors } from '../referralSending';
import useIcs2ReferralDocuments from './useIcs2ReferralDocuments';
import useIcs2ReferralSubmission from './useIcs2ReferralSubmission';

/** Every IE3Q02/IE3Q03 on a declaration, their evidence, and the answers being drafted against them. */
const useIcs2CustomsRequests = (declarationId?: string) => {
    const [requests, setRequests] = useState<Ics2CustomsRequest[]>([]);
    const [submissions, setSubmissions] = useState<Ics2ReferralSubmission[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [loadError, setLoadError] = useState<string>();

    const documents = useIcs2ReferralDocuments(declarationId);

    const load = useCallback(
        async (isRefresh = false) => {
            if (!declarationId) {
                setIsLoading(false);
                setLoadError('Customs requests can only be answered from a saved declaration.');
                return;
            }

            (isRefresh ? setIsRefreshing : setIsLoading)(true);
            setLoadError(undefined);
            try {
                // The history is secondary: failing to read it must not hide the requests.
                const [loaded, history] = await Promise.all([
                    listIcs2CustomsRequests(declarationId),
                    getIcs2ReferralResponseHistory(declarationId).catch(() => [] as Ics2ReferralSubmission[]),
                ]);
                setRequests(loaded);
                setSubmissions(history);
            } catch (error) {
                setLoadError(describeReferralError(error, 'Could not load the Customs requests for this declaration.'));
            } finally {
                setIsLoading(false);
                setIsRefreshing(false);
            }
        },
        [declarationId]
    );

    useEffect(() => {
        load();
    }, [load]);

    const { refresh: refreshDocuments } = documents;
    const reload = useCallback(() => Promise.all([load(true), refreshDocuments()]), [load, refreshDocuments]);

    const rows = useMemo<Ics2ReferralRow[]>(
        () => requests.flatMap((request) => request.referrals.map((referral) => ({ request, referral }))),
        [requests]
    );

    const dispatchErrors = useMemo(() => getDispatchErrors(submissions), [submissions]);

    const replaceReferral = useCallback((request: Ics2CustomsRequest, saved: Ics2Referral) => {
        setRequests((current) =>
            current.map((candidate) =>
                candidate.id === request.id
                    ? {
                          ...candidate,
                          referrals: candidate.referrals.map((entry) =>
                              entry.id === saved.id ? { ...entry, ...saved } : entry
                          ),
                      }
                    : candidate
            )
        );
    }, []);

    const submission = useIcs2ReferralSubmission({
        declarationId,
        rows,
        onDraftSaved: replaceReferral,
        onSent: reload,
    });

    return {
        requests,
        rows,
        dispatchErrors,
        documentsByReferral: documents.documents,
        isLoading,
        isRefreshing,
        loadError,
        reload,
        refreshDocuments,
        readLatestDocuments: documents.readLatest,
        ...submission,
    };
};

export default useIcs2CustomsRequests;
